import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Building2, Plus, MapPin, Search, FileText } from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import EmptyState from '../components/shared/EmptyState';
import ErrorState from '../components/shared/ErrorState';
import EmpreendimentoCreateModal from '../components/launches/EmpreendimentoCreateModal';
import { useBusinessUnit } from '../hooks/useBusinessUnit';
import { useAppPermissions } from '../hooks/useAppPermissions';
import { supabase } from '../supabaseClient';
import { USE_META, MIXED_BADGE, REVIEW_BADGE } from '../components/launches/useCategoryMeta';

const PHASE_META = {
  pre_launch:   { label: 'Pré-lançamento', color: 'bg-purple-100 text-purple-700' },
  launch:       { label: 'Lançamento',     color: 'bg-emerald-100 text-emerald-700' },
  construction: { label: 'Construção',     color: 'bg-amber-100 text-amber-700' },
  delivered:    { label: 'Entregue',       color: 'bg-blue-100 text-blue-700' },
};
const PHASE_ORDER = Object.keys(PHASE_META);

// Badges de governança de uso (primário + misto + needs_review). Read-only;
// consome os campos que a view v_empreendimentos_overview já expõe. (Fase 5B)
function UseBadges({ r }) {
  const meta = USE_META[r.primary_use_category] ?? USE_META.unknown;
  const needsReview = r.use_classification_status === 'needs_review' || (r.tipologias_needs_review ?? 0) > 0;
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.color}`}>{meta.label}</span>
      {r.has_mixed_use === true && (
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${MIXED_BADGE}`}>Misto</span>
      )}
      {needsReview && (
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${REVIEW_BADGE}`}>Revisar</span>
      )}
    </div>
  );
}

function fmtBRL(v) {
  if (v == null) return null;
  const n = Number(v);
  if (!isFinite(n)) return null;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

function priceRange(min, max) {
  const a = fmtBRL(min);
  const b = fmtBRL(max);
  if (!a && !b) return '—';
  if (a && b && a !== b) return `${a} – ${b}`;
  return a || b;
}

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}

// Freshness: idade (em dias) do último last_verified_at -> badge colorido.
// ≤30d verde · 31–90d âmbar · >90d vermelho · null -> sem badge.
function freshnessInfo(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  let color;
  if (days <= 30) color = 'bg-emerald-100 text-emerald-700';
  else if (days <= 90) color = 'bg-amber-100 text-amber-700';
  else color = 'bg-red-50 text-red-700';
  return { label: `${days}d`, color };
}

export default function LaunchesEmpreendimentosListPage() {
  const { availableUnits, isLoading: buLoading } = useBusinessUnit();
  const { permissions } = useAppPermissions();
  const canWrite = permissions.includes('launches.write');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [useFilter, setUseFilter] = useState('');
  const [ufFilter, setUfFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [neighborhoodFilter, setNeighborhoodFilter] = useState('');
  const [incorporadoraFilter, setIncorporadoraFilter] = useState('');
  const [precoMin, setPrecoMin] = useState('');
  const [precoMax, setPrecoMax] = useState('');
  const [qualidadeFilter, setQualidadeFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) setSortDir((p) => (p === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('v_empreendimentos_overview')
      .select('*')
      .order('name');
    if (err) {
      setError(err.message);
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!buLoading && availableUnits?.includes('launches')) fetchRows();
  }, [buLoading, availableUnits]);

  // Listas de filtro (geografia encadeada UF -> Cidade -> Bairro)
  const ufs = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.state) s.add(r.state); });
    return Array.from(s).sort();
  }, [rows]);

  const cities = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.city && (!ufFilter || r.state === ufFilter)) s.add(r.city); });
    return Array.from(s).sort();
  }, [rows, ufFilter]);

  const neighborhoods = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => {
      if (r.neighborhood && (!ufFilter || r.state === ufFilter) && (!cityFilter || r.city === cityFilter)) s.add(r.neighborhood);
    });
    return Array.from(s).sort();
  }, [rows, ufFilter, cityFilter]);

  const uses = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.primary_use_category) s.add(r.primary_use_category); });
    return Array.from(s).sort();
  }, [rows]);

  const incorporadoraNomes = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { (r.incorporadora_nomes || []).forEach((n) => { if (n) s.add(n); }); });
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const min = precoMin !== '' ? Number(precoMin) : null;
    const max = precoMax !== '' ? Number(precoMax) : null;
    const precoActive = min != null || max != null;
    return rows.filter((r) => {
      if (phaseFilter && r.phase !== phaseFilter) return false;
      if (useFilter && r.primary_use_category !== useFilter) return false;
      if (ufFilter && r.state !== ufFilter) return false;
      if (cityFilter && r.city !== cityFilter) return false;
      if (neighborhoodFilter && r.neighborhood !== neighborhoodFilter) return false;
      if (incorporadoraFilter && !(r.incorporadora_nomes?.includes(incorporadoraFilter))) return false;
      if (precoActive) {
        if (r.price_min == null) return false;
        if (min != null && !(r.price_max >= min)) return false;
        if (max != null && !(r.price_min <= max)) return false;
      }
      if (qualidadeFilter === 'review') {
        if (!(r.use_classification_status === 'needs_review' || (r.tipologias_needs_review ?? 0) > 0)) return false;
      } else if (qualidadeFilter === 'incompleto') {
        if (!((r.tipologias_count ?? 0) === 0 || r.price_min == null)) return false;
      }
      if (needle) {
        const bag = [r.name, r.short_name, r.incorporadoras, r.neighborhood, r.city, r.state, r.address_street, r.address_number]
          .filter(Boolean).join(' ').toLowerCase();
        if (!bag.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, search, phaseFilter, useFilter, ufFilter, cityFilter, neighborhoodFilter, incorporadoraFilter, precoMin, precoMax, qualidadeFilter]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let va; let vb;
      switch (sortField) {
        case 'name':          va = a.name || '';                 vb = b.name || '';                 break;
        case 'neighborhood':  va = a.neighborhood || '';         vb = b.neighborhood || '';         break;
        case 'city':          va = a.city || '';                 vb = b.city || '';                 break;
        case 'state':         va = a.state || '';                vb = b.state || '';                break;
        case 'phase':         va = PHASE_ORDER.indexOf(a.phase); vb = PHASE_ORDER.indexOf(b.phase); break;
        case 'use':           va = a.primary_use_category || ''; vb = b.primary_use_category || ''; break;
        case 'incorporadora': va = a.incorporadoras || '';       vb = b.incorporadoras || '';       break;
        case 'tipologias':    va = a.tipologias_count ?? -1;     vb = b.tipologias_count ?? -1;     break;
        case 'entrega':       va = a.expected_delivery || '';    vb = b.expected_delivery || '';    break;
        case 'preco':         va = a.price_min ?? -1;            vb = b.price_min ?? -1;            break;
        case 'materiais':     va = a.materials_count ?? -1;      vb = b.materials_count ?? -1;      break;
        case 'freshness':     va = a.last_verified_at || '';     vb = b.last_verified_at || '';     break;
        default: return 0;
      }
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [filtered, sortField, sortDir]);

  const hasFilters = !!(search || phaseFilter || useFilter || ufFilter || cityFilter ||
    neighborhoodFilter || incorporadoraFilter || precoMin || precoMax || qualidadeFilter);

  const clearAll = () => {
    setSearch(''); setPhaseFilter(''); setUseFilter(''); setUfFilter(''); setCityFilter('');
    setNeighborhoodFilter(''); setIncorporadoraFilter(''); setPrecoMin(''); setPrecoMax(''); setQualidadeFilter('');
  };

  const SortHeader = ({ field, label, align = 'text-left' }) => (
    <th
      className={`px-2 py-1.5 ${align} cursor-pointer select-none whitespace-nowrap hover:text-violet-700`}
      onClick={() => handleSort(field)}
    >
      {label}{sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  if (buLoading) {
    return (
      <>
        <AppHeader title="Empreendimentos" icon={Building2} />
        <div className="p-6"><LoadingSpinner /></div>
      </>
    );
  }
  if (!availableUnits || !availableUnits.includes('launches')) {
    return <Navigate to="/" replace />;
  }

  const selectCls = 'text-xs border border-slate-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500';

  return (
    <>
      <AppHeader title="Empreendimentos" subtitle={`${filtered.length} de ${rows.length}`} icon={Building2} />
      <div className="p-4 lg:p-6 space-y-3">
        <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-2.5 top-2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar nome, incorporadora, endereço..."
                className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              disabled={!canWrite}
              title={canWrite ? 'Criar novo empreendimento' : 'Sem permissão de escrita.'}
              className="px-2.5 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-xs flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              <Plus size={13} /> Novo empreendimento
            </button>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)} className={selectCls}>
              <option value="">Todas as fases</option>
              {Object.entries(PHASE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={useFilter} onChange={(e) => setUseFilter(e.target.value)} className={selectCls} disabled={uses.length === 0}>
              <option value="">Todos os usos</option>
              {uses.map((u) => <option key={u} value={u}>{USE_META[u]?.label ?? u}</option>)}
            </select>
            <select
              value={ufFilter}
              onChange={(e) => { setUfFilter(e.target.value); setCityFilter(''); setNeighborhoodFilter(''); }}
              className={selectCls}
              disabled={ufs.length === 0}
            >
              <option value="">Todas as UFs</option>
              {ufs.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <select
              value={cityFilter}
              onChange={(e) => { setCityFilter(e.target.value); setNeighborhoodFilter(''); }}
              className={selectCls}
              disabled={cities.length === 0}
            >
              <option value="">Todas as cidades</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={neighborhoodFilter} onChange={(e) => setNeighborhoodFilter(e.target.value)} className={selectCls} disabled={neighborhoods.length === 0}>
              <option value="">Todos os bairros</option>
              {neighborhoods.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <select value={incorporadoraFilter} onChange={(e) => setIncorporadoraFilter(e.target.value)} className={selectCls} disabled={incorporadoraNomes.length === 0}>
              <option value="">Todas as incorporadoras</option>
              {incorporadoraNomes.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <select value={qualidadeFilter} onChange={(e) => setQualidadeFilter(e.target.value)} className={selectCls}>
              <option value="">Qualidade: todas</option>
              <option value="review">Precisa revisão</option>
              <option value="incompleto">Incompleto</option>
            </select>
            <input
              type="number"
              value={precoMin}
              onChange={(e) => setPrecoMin(e.target.value)}
              placeholder="R$ mín"
              className="text-xs border border-slate-300 rounded px-2 py-1.5 bg-white w-24 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
            <input
              type="number"
              value={precoMax}
              onChange={(e) => setPrecoMax(e.target.value)}
              placeholder="R$ máx"
              className="text-xs border border-slate-300 rounded px-2 py-1.5 bg-white w-24 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
            {hasFilters && (
              <button onClick={clearAll} className="text-[11px] text-slate-500 hover:text-slate-800 underline">limpar</button>
            )}
          </div>
        </div>

        {loading && <LoadingSpinner />}
        {!loading && error && <ErrorState message={error} />}
        {!loading && !error && filtered.length === 0 && (
          <EmptyState
            icon={Building2}
            message={rows.length === 0 ? 'Nenhum empreendimento cadastrado' : 'Nenhum empreendimento corresponde aos filtros'}
            hint={rows.length === 0 && canWrite ? 'Crie o primeiro com o botão acima.' : null}
          />
        )}

        {!loading && !error && filtered.length > 0 && (
          <>
            <div className="hidden md:block bg-white border border-slate-200 rounded overflow-x-auto">
              <table className="w-full text-xs min-w-[1180px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <SortHeader field="name" label="Nome" />
                    <SortHeader field="neighborhood" label="Bairro" />
                    <SortHeader field="city" label="Cidade" />
                    <SortHeader field="state" label="UF" />
                    <SortHeader field="phase" label="Fase" />
                    <SortHeader field="use" label="Uso" />
                    <SortHeader field="incorporadora" label="Incorporadora" />
                    <SortHeader field="tipologias" label="Tipologias" align="text-right" />
                    <SortHeader field="entrega" label="Entrega" />
                    <SortHeader field="preco" label="Preço" align="text-right" />
                    <SortHeader field="materiais" label="Materiais" align="text-right" />
                    <SortHeader field="freshness" label="Freshness" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sorted.map((r) => {
                    const meta = PHASE_META[r.phase] || { label: r.phase, color: 'bg-slate-100 text-slate-700' };
                    const fresh = freshnessInfo(r.last_verified_at);
                    return (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-2 py-1.5">
                          <Link to={`/launches/empreendimentos/${r.id}`} className="text-violet-700 hover:underline font-medium">
                            {r.name || '(sem nome)'}
                          </Link>
                          {r.short_name && <div className="text-[10px] text-slate-400">{r.short_name}</div>}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{r.neighborhood || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.city || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.state || '—'}</td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.color}`}>{meta.label}</span>
                        </td>
                        <td className="px-2 py-1.5"><UseBadges r={r} /></td>
                        <td className="px-2 py-1.5 text-slate-600">{r.incorporadoras || '—'}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">{r.tipologias_count ?? '—'}</td>
                        <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{fmtDate(r.expected_delivery)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700 whitespace-nowrap">{priceRange(r.price_min, r.price_max)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-700">
                          {(r.materials_count ?? 0) > 0
                            ? <span className="inline-flex items-center gap-1 justify-end"><FileText size={11} className="text-slate-400" />{r.materials_count}</span>
                            : '—'}
                        </td>
                        <td className="px-2 py-1.5">
                          {fresh
                            ? <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${fresh.color}`}>{fresh.label}</span>
                            : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-2">
              {sorted.map((r) => {
                const meta = PHASE_META[r.phase] || { label: r.phase, color: 'bg-slate-100 text-slate-700' };
                const fresh = freshnessInfo(r.last_verified_at);
                const cityUf = [r.city, r.state].filter(Boolean).join('/');
                const localidade = [r.neighborhood, cityUf].filter(Boolean).join(' · ');
                return (
                  <Link
                    key={r.id}
                    to={`/launches/empreendimentos/${r.id}`}
                    className="block bg-white border border-slate-200 rounded p-3 hover:border-violet-300"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-violet-700 truncate">{r.name || '(sem nome)'}</div>
                        {r.short_name && <div className="text-[10px] text-slate-400">{r.short_name}</div>}
                      </div>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${meta.color}`}>{meta.label}</span>
                    </div>
                    <div className="mt-1.5"><UseBadges r={r} /></div>
                    {localidade && (
                      <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
                        <MapPin size={10} />{localidade}
                      </div>
                    )}
                    {r.incorporadoras && (
                      <div className="mt-1 text-[11px] text-slate-600 truncate">{r.incorporadoras}</div>
                    )}
                    <div className="mt-1 text-[11px] text-slate-600">
                      {(r.tipologias_count ?? 0)} tipologia{r.tipologias_count === 1 ? '' : 's'} · {priceRange(r.price_min, r.price_max)}
                    </div>
                    {(r.expected_delivery || (r.materials_count ?? 0) > 0 || fresh) && (
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                        {r.expected_delivery && <span>Entrega {fmtDate(r.expected_delivery)}</span>}
                        {(r.materials_count ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1"><FileText size={10} className="text-slate-400" />{r.materials_count}</span>
                        )}
                        {fresh && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${fresh.color}`}>{fresh.label}</span>}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>

      <EmpreendimentoCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); fetchRows(); }}
      />
    </>
  );
}
